import {
  useListAtsNeedsMerge,
  useResolveAtsNeedsMerge,
} from "@/lib/api/generated";
import type { ResolveMergeRequest } from "@/lib/api/model/resolveMergeRequest";
import { useJobPipelineStreamConnected } from "@/features/jobs/hooks/job-pipeline-stream-context";
import type { AtsNeedsMergeItem } from "../types";

export function useNeedsMerge(jobId: string | null) {
  // When the SSE stream is live, `needs_merge_changed` / `sync_run_completed`
  // events drive invalidation, so the 15s poll stands down (self-heals to
  // polling if the stream drops — the context flips back to false).
  const streamConnected = useJobPipelineStreamConnected();
  const query = useListAtsNeedsMerge(jobId ?? "", {
    query: {
      enabled: !!jobId,
      refetchInterval: streamConnected ? false : 15_000,
    },
  });
  const items: AtsNeedsMergeItem[] = (query.data?.items ?? []).map((it) => ({
    id: it.link_id,
    ats_candidate: {
      name: it.ats_candidate.name ?? "",
      email: it.ats_candidate.email ?? null,
      remote_id: it.ats_candidate.remote_id,
    },
    candidates: (it.candidates ?? []).map((c) => ({
      profile_id: c.profile_id,
      name: c.name ?? "",
      email: c.email ?? null,
    })),
  }));
  return {
    items,
    count: items.length,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useResolveMerge(jobId: string) {
  const mutation = useResolveAtsNeedsMerge();
  return {
    ...mutation,
    mutateAsync: (vars: { linkId: string; data: ResolveMergeRequest }) =>
      mutation.mutateAsync({ jobId, linkId: vars.linkId, data: vars.data }),
    mutate: (vars: { linkId: string; data: ResolveMergeRequest }) =>
      mutation.mutate({ jobId, linkId: vars.linkId, data: vars.data }),
  };
}
